"use client";
import { getProviderDisplayName } from "../lib/providerName";
import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import Button from "../components/Button";
import Spinner from "../components/Spinner";

function QueryMessage() {
  // Get message from URL parameters
  const sp = useSearchParams();
  const message = sp?.get("message");
  if (!message) return null;
  return (
    <div
      style={{
        backgroundColor: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: "0.375rem",
        padding: "0.75rem",
        marginTop: "1rem",
        marginBottom: "1rem",
      }}
    >
      <div style={{ color: "#15803D", fontSize: "0.875rem" }}>{message}</div>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const router = useRouter();
  const supabase = createClientComponentClient();

  const login = async () => {
    if (!email || !password) {
      setError("Email a heslo sú povinné");
      return;
    }
    setLoading(true);
    setError("");

    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError?.code) {
      if (authError.code === "invalid_credentials") {
        setError("Nesprávny email alebo heslo");
      } else {
        setError(authError.message);
      }
      setLoading(false);
      return;
    }

    // Ask the API who this is, rather than reading clinic_staff from the
    // browser. That query was the only thing in the app still touching a table
    // with the public key, and keeping it meant leaving a policy open for it.
    // The endpoint resolves the staff row from the session server-side.
    let me: {
      providerId: string;
      clinicId: string;
      clinicName: string | null;
      clinicSlug: string | null;
      title: string | null;
      first_name: string | null;
      last_name: string | null;
    };
    try {
      const res = await fetch("/api/admin/me");
      if (!res.ok) throw new Error(String(res.status));
      me = await res.json();
    } catch {
      setError("Nenašiel sa účet poskytovateľa pre tohto používateľa");
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // Cached into user_metadata for display only — it is browser-writable, so
    // nothing may trust it for access decisions. /api/admin/me stays the source.
    await supabase.auth.updateUser({
      data: {
        clinic_id: me.clinicId,
        clinic_slug: me.clinicSlug,
        clinic_name: me.clinicName,
        provider_id: me.providerId,
        provider_name: getProviderDisplayName({
          title: me.title,
          first_name: me.first_name,
          last_name: me.last_name,
        }),
      },
    });

    setLoading(false);

    router.push("/admin");
  };

  return (
    <div
      style={{ width: "100%", minHeight: "100vh", backgroundColor: "#fff" }}
      className="shadow-border"
    >
      <div className="container">
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            backgroundImage: 'url("/images/login-bg.jpeg")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.3,
            zIndex: 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        />

        {/* On the photograph, top left. It used to sit in a white band above
            it, which cost 100px of the image for no reason. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/ordinex-logo.png"
          alt="Ordinex"
          style={{
            position: "absolute",
            top: 24,
            left: 24,
            width: 200,
            zIndex: 2,
          }}
        />
        <div
          style={{
            width: 500,
            padding: 20,
            position: "relative",
            zIndex: 1,
          }}
          className="shadow-border card"
        >
          <h2 style={{ textAlign: "center" }}>Prihlásenie</h2>
          <Suspense fallback={null}>
            <QueryMessage />
          </Suspense>
          {error && (
            <div
              style={{
                backgroundColor: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: "0.375rem",
                padding: "0.75rem",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ color: "#B91C1C", fontSize: "0.875rem" }}>
                {error}
              </div>
            </div>
          )}
          <form>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                marginTop: 20,
                marginBottom: 20,
              }}
            >
              <label htmlFor="email" style={{ fontSize: 14 }}>
                Email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                id="email"
                name="email"
                data-testid="email-input"
                placeholder="Zadajte email"
                style={{ backgroundColor: "#F3F4F6FF" }}
                disabled={loading}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                marginTop: 20,
                marginBottom: 20,
              }}
            >
              <label htmlFor="password" style={{ fontSize: 14 }}>
                Heslo
              </label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                id="password"
                name="password"
                placeholder="Zadajte heslo"
                style={{ backgroundColor: "#F3F4F6FF" }}
                disabled={loading}
                data-testid="password-input"
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 13,
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <input type="checkbox" id="remember" name="remember" />
                <label htmlFor="remember">Zapamätať si ma</label>
              </div>
              <a href="/forgot-password" style={{ color: "#0099E0FF" }}>
                Zabudli ste heslo?
              </a>
            </div>
            <Button
              variant="primary"
              style={{ width: "100%", justifyContent: "center" }}
              disabled={loading || !email || !password}
              data-testid="login-button"
              onClick={login}
            >
              {loading ? "Prihlasuje sa..." : "Prihlásiť sa"}
            </Button>
          </form>

          <div style={{ textAlign: "center", marginTop: 20, fontSize: 13 }}>
            Nemáte účet?{" "}
            <a href="/registration" style={{ color: "#0099E0FF" }}>
              Zaregistrovať sa
            </a>
          </div>
        </div>
      </div>
      {loading && <Spinner />}
      <style>{`
      .container {
        position: relative;
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      /* Shift to the right on large screens */
      @media (min-width: 1024px) {
        .container {
          // justify-content: flex-end;  /* stick to the right */
          // padding-right: 6vw;         /* breathing room from edge */
          padding-left: 450px;
        }
      }
    `}</style>
    </div>
  );
}
